"use client";

import { AppBar, Card, CircularProgress, Tab } from "@mui/material";
import React from "react";
import { useParams } from "react-router-dom";

import Tabs from "@/components/atoms/ZoomAwareTabs";
import MDBox from "@/components/atoms/MDBox";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetContactById from "@/hooks/requests/contact/use-get-contact-by-id";
import useTabs from "@/hooks/use-tabs";
import { ContactFormPageMessages } from "@/messages/contacts/contact-form-page.messages";
import pxToRem from "@/themes/functions/pxToRem";

import ChatSection from "./components/ChatSection";
import PersonalDetailsTab from "./components/PersonalDetailsTab";
import { TABS_ITEMS } from "./constants";
import { ContactTabs } from "./types";

type Props = {
  messages: ContactFormPageMessages;
  isEditing?: boolean;
};

export default function ContactFormPage({ messages, isEditing }: Props) {
  const { contactId } = useParams();
  const { lang } = useLanguage();
  const _id = Number(contactId) || 0;
  const { data: contact, isLoading, error } = useGetContactById({ _id });
  const TABS = TABS_ITEMS(messages, !!isEditing);
  const tabs = useTabs<ContactTabs>({ initialTab: "personalDetailsTab", tabs: TABS, isEditing });

  const { activeTab, changeTab, isTabDisabled } = tabs;

  const isPersonalDetailsTab = activeTab === "personalDetailsTab";
  const isChatTab = activeTab === "chatTab" && isEditing && contact;
  const shouldLoadError = !contact && !!error && !isLoading;
  const pageTitle = isEditing ? messages.contactFormPageTitle : messages.createContactPageTitle;

  const BREAD_CRUMBS = [{ title: messages.breadCrumbTitle, href: ROUTE_URLS.contacts(lang) }];

  const tabSize = isEditing ? 400 : 200;

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isLoading}>
          {
            <AppBar position="static">
              <Tabs
                value={activeTab}
                onChange={(_, newTab) => {
                  if (!isTabDisabled(newTab)) changeTab(newTab);
                }}
                sx={{ maxWidth: pxToRem(tabSize), width: { xs: "100%" } }}
              >
                {TABS.map((tab, index) => (
                  <Tab
                    key={index}
                    label={tab.label}
                    value={tab.value}
                    disabled={isTabDisabled(tab.value)}
                  />
                ))}
              </Tabs>
            </AppBar>
          }

          {isPersonalDetailsTab && (
            <PersonalDetailsTab contact={contact} messages={messages} isEditing={isEditing} />
          )}

          {isChatTab && <ChatSection messages={messages} contact={contact} />}

          {!!shouldLoadError && (
            <MDBox mt={2}>
              <Card>
                <AlertBlock
                  title={messages.contactErrorToLoad}
                  description={error.message}
                  alt={messages.contactNotFoundAlt}
                />
              </Card>
            </MDBox>
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}
